import React from "react";
import { useMediaQuery } from "@mui/system";
import { Box, Table, TableBody, TableContainer } from "@mui/material";
import { SectionTitle } from "features/SectionTitle";
import {
  DateField,
  FunctionField,
  NumberField,
  RecordContextProvider,
  ReferenceField,
  TextField,
  useRecordContext,
} from "react-admin";
import { settings } from "app";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";
import { BusinessTripMealProvidedChoices } from "utils/choices";
import {
  BusinessTripTypes,
  DetailsLeaveType,
  PERSONAL_CAR_TRANSPORT_TYPES,
  SETTLEMENT_WITH_AREA_QUERY,
} from "utils/constants";
import InfoRow from "./InfoRow";
import TravelersTable from "./TravelersTable";

const LocationCityField = () => {
  const location = useRecordContext();
  if (location?.settlementId) {
    return (
      <ReferenceField
        source="settlementId"
        reference={administrationRequests.getSettlementsSelect()}
        queryOptions={{ meta: { query: SETTLEMENT_WITH_AREA_QUERY } }}
        link={false}
      >
        <TextField source="nameBg" />
      </ReferenceField>
    );
  }
  return <TextField source="settlement" />;
};

const LocationsField = () => {
  const record = useRecordContext();
  const locations = record?.locations ?? [];

  return (
    <>
      {locations.map((location, index) => (
        <RecordContextProvider
          key={index}
          value={location}
        >
          <LocationCityField />
          {", "}
          <ReferenceField
            source="countryId"
            reference={administrationRequests.getCountriesSelect()}
            link={false}
          >
            <TextField source="nameBg" />
          </ReferenceField>
          {index < locations.length - 1 ? "; " : ""}
        </RecordContextProvider>
      ))}
    </>
  );
};

const BusinessTripInfoSection = ({ type }) => {
  const record = useRecordContext();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const leaveTypeName = DetailsLeaveType.find(x => x.id === type)?.name;
  const isDomestic = record?.businessTripType === BusinessTripTypes.Internal;
  const isPersonalCar = PERSONAL_CAR_TRANSPORT_TYPES.includes(record?.transportType);

  return (
    <>
      <SectionTitle
        title={`Данни за заявката${leaveTypeName ? `: ${leaveTypeName}` : ""}`}
        textAlign="center"
        sx={{ marginTop: "0.5rem" }}
      />
      <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {record?.registryNumber && (
                <InfoRow label="Регистрационен номер">
                  <TextField source="registryNumber" />
                </InfoRow>
              )}
              <InfoRow label="Тип командировка">
                <ReferenceField
                  source="businessTripType"
                  reference={employeeRequests.getBusinessTripsSelect()}
                  link={false}
                >
                  <TextField source="nameBg" />
                </ReferenceField>
              </InfoRow>
              {isDomestic && (
                <InfoRow label="Тип вътрешна командировка">
                  <ReferenceField
                    source="domesticTripCategory"
                    reference={employeeRequests.getDomesticTripCategoriesSelect()}
                    link={false}
                  >
                    <TextField source="nameBg" />
                  </ReferenceField>
                </InfoRow>
              )}
              <InfoRow label="Тип транспорт">
                <ReferenceField
                  source="transportType"
                  reference={employeeRequests.getLeaveTransportsSelect()}
                  link={false}
                >
                  <TextField source="nameBg" />
                </ReferenceField>
              </InfoRow>
              {isPersonalCar && (
                <InfoRow label="Вид и марка на МПС">
                  <TextField source="vehicleTypeAndMake" />
                </InfoRow>
              )}
              {isPersonalCar && (
                <InfoRow label="Вид гориво">
                  <TextField source="fuelType" />
                </InfoRow>
              )}
              {isPersonalCar && (
                <InfoRow label="Среден разход на гориво">
                  <NumberField source="averageFuelConsumption" />
                </InfoRow>
              )}
              {isPersonalCar && (
                <InfoRow label="Цена на горивото">
                  <NumberField
                    source="fuelPrice"
                    locales="bg-BG"
                    options={{ style: "currency", currency: "EUR", currencyDisplay: "code" }}
                  />
                </InfoRow>
              )}
              {isPersonalCar && (
                <InfoRow label="Разстояние (км)">
                  <NumberField source="distanceInKm" />
                </InfoRow>
              )}
              <InfoRow label="От дата">
                <DateField
                  source="dateFrom"
                  locales="bg-BG"
                />
              </InfoRow>
              <InfoRow label="До дата">
                <DateField
                  source="dateTo"
                  locales="bg-BG"
                />
              </InfoRow>
              {!isDomestic && (
                <InfoRow label="Локации">
                  <LocationsField />
                </InfoRow>
              )}
              <InfoRow label="По маршрут">
                <TextField source="route" />
              </InfoRow>
              <InfoRow label="Осигурено изхранване">
                <FunctionField
                  render={rec =>
                    BusinessTripMealProvidedChoices.find(x => x.id === rec?.mealProvided)?.name ?? "-"
                  }
                />
              </InfoRow>
              <InfoRow label="Дневни пари на ден">
                <NumberField
                  source="dailyAllowancePerDay"
                  locales="bg-BG"
                  options={{ style: "currency", currency: "EUR", currencyDisplay: "code" }}
                />
              </InfoRow>
              <InfoRow label="Брой дни с дневни пари">
                <NumberField source="dailyAllowanceDays" />
              </InfoRow>
              <InfoRow label="Макс. сума за нощувка">
                <NumberField
                  source="maxAccommodationAmountPerNight"
                  locales="bg-BG"
                  options={{ style: "currency", currency: "EUR", currencyDisplay: "code" }}
                />
              </InfoRow>
              <InfoRow label="Брой нощувки">
                <NumberField source="nightsCount" />
              </InfoRow>
              <InfoRow label="Платец на разходите">
                <TextField source="expensePayer" />
              </InfoRow>
              <InfoRow label={isDomestic ? "Задача" : "Причина за командировката"}>
                <TextField source="assignment" />
              </InfoRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <TravelersTable />
    </>
  );
};

export default BusinessTripInfoSection;
